import { useMountEffect } from './useMountEffect';

/**
 * Reference-counted body scroll lock.
 *
 * Multiple modals/popovers/drawers can be open at once; each calls
 * `useBodyScrollLock()` and contributes one to the lock count. Body overflow
 * is restored only when the count returns to zero, so closing an inner
 * modal while an outer one is still open does NOT prematurely unlock.
 *
 * The original overflow value is captured the first time the count goes
 * from 0 → 1, ensuring nested locks restore the right value on the way out.
 */

let lockCount = 0;
let previousOverflow: string | null = null;
let previousPaddingRight: string | null = null;

function applyLock(): void {
    if (lockCount === 0) {
        previousOverflow = document.body.style.overflow;
        previousPaddingRight = document.body.style.paddingRight;
        // Compensate for scrollbar disappearance to avoid layout jump.
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        document.body.style.overflow = 'hidden';
    }
    lockCount += 1;
}

function releaseLock(): void {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
        document.body.style.overflow = previousOverflow ?? '';
        document.body.style.paddingRight = previousPaddingRight ?? '';
        previousOverflow = null;
        previousPaddingRight = null;
    }
}

export function useBodyScrollLock(): void {
    useMountEffect(() => {
        applyLock();
        return () => releaseLock();
    });
}
